import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Upload, Button, Form, Input, Select, Tooltip, Popconfirm } from 'antd';
import {
  FieldNumberOutlined,
  HomeOutlined,
  UploadOutlined,
  ShopOutlined,
  BankOutlined,
  UserAddOutlined,
  UserSwitchOutlined,
  UnlockOutlined,
  LockOutlined,
  PhoneOutlined,
  UserOutlined,
  NodeIndexOutlined,
  AimOutlined,
  CompassOutlined,
  QuestionCircleOutlined,
  CreditCardOutlined,
  PrinterOutlined,
  WarningOutlined
} from '@ant-design/icons';
import IBAN from 'iban';
import { useTranslation } from 'react-i18next';
import useXmlPattern from '../../utils/xmlPattern';
import RegisterCondition from './RegisterCondition';
import Universign from './Universign';
import useHandleResize from '../../utils/HandleResize';
import usePwdPattern from '../../utils/pwdPattern';

const { Option } = Select;

const toInputUppercase = (e) => {
  e.target.value = `${e.target.value}`.toUpperCase();
};

const RegisterInput = ({
  isLoading,
  handleSiretChange,
  handleOriasChange,
  handlePostalCodeChange,
  cities,
  getLayoutFromOriasStatus,
  enums,
  oriasStatus,
  siretStatus,
  setCNI,
  setKbis,
  setOrias,
  setRib,
  current,
  setEmail,
  insurer,
  printConvention,
  rib,
  cni,
  kbis,
  orias,
  errorGetCities
}) => {
  const [upperIban, setUpperIban] = useState('');
  const { t } = useTranslation();
  const xmlPattern = useXmlPattern();
  const { width } = useHandleResize();
  const pwdPattern = usePwdPattern();

  return (
    <div id="mainContent">
      {current === 0 ? (
        <>
          <Form.Item
            name="owner_gender"
            label={<UserSwitchOutlined />}
            rules={[{ required: true, message: t('errors.form.REQUIRED') }]}
          >
            <Select
              style={{ width: '100%', textAlign: 'left', borderRadius: 5 }}
              placeholder={t('insurers.form.owner_gender')}
            >
              {enums.genders &&
                enums.genders.map((g) => (
                  <Option value={g} key={g}>
                    {t(`insurers.form.${g}`)}
                  </Option>
                ))}
            </Select>
          </Form.Item>
          <Form.Item
            name="owner_first_name"
            label={<UserOutlined />}
            rules={[
              { required: true, message: t('errors.form.REQUIRED') },
              ...xmlPattern
            ]}
          >
            <Input placeholder={t('insurers.form.owner_first_name')} />
          </Form.Item>
          <Form.Item
            name="owner_last_name"
            label={<UserAddOutlined />}
            rules={[
              { required: true, message: t('errors.form.REQUIRED') },
              ...xmlPattern
            ]}
          >
            <Input placeholder={t('insurers.form.owner_last_name')} />
          </Form.Item>
          <Form.Item
            name="owner_email"
            label="@"
            rules={[
              { required: true, type: 'email', message: t('errors.form.EMAIL') }
            ]}
          >
            <Input
              placeholder={`${t('insurers.form.owner_email_register')}`}
              onChange={(event) => setEmail(event.target.value)}
            />
          </Form.Item>
          <Form.Item
            name="password"
            label={<UnlockOutlined />}
            rules={[
              { required: true, message: t('profile.form.pwdMissing') },
              ...pwdPattern
            ]}
            hasFeedback
          >
            <Input.Password placeholder={`${t('login.password')}`} />
          </Form.Item>
          <Form.Item
            dependencies={['password']}
            hasFeedback
            name="confirm"
            label={<LockOutlined />}
            rules={[
              {
                required: true,
                message: t('profile.form.pwdMissing')
              },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('password') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(t('login.pwdNotMatching'));
                }
              })
            ]}
          >
            <Input.Password placeholder={`${t('login.confirmPassword')}`} />
          </Form.Item>
          <Form.Item
            name="owner_phone_number"
            label={<PhoneOutlined />}
            rules={[
              {
                required: true,
                message: t('errors.form.PHONE')
              },
              { len: 10 }
            ]}
          >
            <Input
              minLength={10}
              maxLength={10}
              placeholder={`${t(
                'insurers.form.owner_phone_number'
              )} (signature électronique)`}
            />
          </Form.Item>
        </>
      ) : (
        []
      )}

      {current === 1 ? (
        <>
          <Form.Item
            name="orias_number"
            label={<FieldNumberOutlined />}
            rules={[{ required: true, message: t('errors.form.REQUIRED') }]}
            hasFeedback={
              oriasStatus === 'SUPPRIME' || oriasStatus === 'INSCRIT'
            }
            layout={oriasStatus && getLayoutFromOriasStatus(oriasStatus)}
          >
            <Input
              onChange={handleOriasChange}
              id="success"
              placeholder={t('login.orias')}
              maxLength={8}
            />
          </Form.Item>
          <Form.Item
            name="siret_number"
            label={<FieldNumberOutlined />}
            rules={[{ required: true, message: t('errors.form.SIRET') }]}
            hasFeedback={siretStatus}
          >
            <Input
              suffix={
                <Tooltip
                  title={t(`contracts.form.tooltip.siret`)}
                  overlayInnerStyle={{ width: 180 }}
                >
                  <QuestionCircleOutlined style={{ float: 'right' }} />
                </Tooltip>
              }
              minLength={14}
              maxLength={14}
              onChange={handleSiretChange}
              placeholder={t('login.siret')}
            />
          </Form.Item>
          <Form.Item
            name="name"
            label={<ShopOutlined />}
            rules={[
              { required: true, message: t('errors.form.REQUIRED') },
              ...xmlPattern
            ]}
          >
            <Input placeholder={t('insurers.form.name')} />
          </Form.Item>
          <Form.Item
            name="category_insurer"
            label={<BankOutlined />}
            rules={[{ required: true, message: t('errors.form.REQUIRED') }]}
          >
            <Input placeholder={t('insurers.form.category_insurer')} />
          </Form.Item>
          <Form.Item
            name="email"
            label="@"
            rules={[
              { required: true, type: 'email', message: t('errors.form.EMAIL') }
            ]}
          >
            <Input placeholder={t('insurers.form.email')} />
          </Form.Item>
          <Form.Item
            name="phone_number"
            label={<PhoneOutlined />}
            rules={[
              {
                required: true,
                message: t('errors.form.PHONE')
              },
              { len: 10 }
            ]}
          >
            <Input
              minLength={10}
              maxLength={10}
              placeholder={t('insurers.form.phone_number')}
            />
          </Form.Item>

          <Form.Item
            name="iban"
            label={<CreditCardOutlined />}
            rules={[
              { required: true, message: t('errors.form.REQUIRED') },
              () => ({
                validator(_, value) {
                  if (IBAN.isValid(value)) return Promise.resolve();
                  // eslint-disable-next-line prefer-promise-reject-errors
                  return Promise.reject('IBAN invalide');
                }
              })
            ]}
          >
            <Input
              value={upperIban}
              onChange={(e) => setUpperIban(e.target.value)}
              onInput={toInputUppercase}
              placeholder={t('insurers.form.iban_simple')}
            />
          </Form.Item>
          <Form.Item
            label={<CreditCardOutlined />}
            rules={[{ required: true, message: t('errors.form.REQUIRED') }]}
            name="bic"
          >
            <Select
              showSearch
              style={{ width: '100%', textAlign: 'left', borderRadius: 5 }}
              placeholder={t('insurers.form.bic_simple')}
            >
              {enums.genders &&
                enums?.bics?.map((g) => (
                  <Option value={g} key={g}>
                    {t(`${g}`)}
                  </Option>
                ))}
            </Select>
          </Form.Item>
          <Form.Item
            name="CNI"
            label={t('insurers.form.id_card')}
            rules={[{ required: true, message: t('errors.form.REQUIRED') }]}
            style={{
              textAlign: 'right',
              marginTop: 9
            }}
          >
            <Upload
              multiple
              maxCount={3}
              beforeUpload={(file) => {
                const newFile = [...cni];
                newFile.push(file);
                setCNI(newFile);
                return false;
              }}
              onRemove={(doc) => {
                const index = cni.indexOf(doc);
                const newFile = cni.slice();
                newFile.splice(index, 1);
                setCNI(newFile);
              }}
            >
              <Button icon={<UploadOutlined />}>{t('buttons.upload')}</Button>
            </Upload>
          </Form.Item>
          <Form.Item
            name="KBIS"
            label={t('insurers.form.KBIS')}
            rules={[{ required: true, message: t('errors.form.REQUIRED') }]}
            style={{ textAlign: 'right' }}
          >
            <Upload
              multiple
              maxCount={3}
              beforeUpload={(file) => {
                const newFile = [...kbis];
                newFile.push(file);
                setKbis(newFile);
                return false;
              }}
              onRemove={(doc) => {
                const index = kbis.indexOf(doc);
                const newFile = kbis.slice();
                newFile.splice(index, 1);
                setKbis(newFile);
              }}
            >
              <Button icon={<UploadOutlined />}>{t('buttons.upload')}</Button>
            </Upload>
          </Form.Item>
          <Form.Item
            name="ORIAS"
            label={t('insurers.form.ORIAS')}
            rules={[{ required: true, message: t('errors.form.REQUIRED') }]}
            style={{ textAlign: 'right' }}
          >
            <Upload
              multiple
              maxCount={3}
              beforeUpload={(file) => {
                const newFile = [...orias];
                newFile.push(file);
                setOrias(newFile);
                return false;
              }}
              onRemove={(doc) => {
                const index = orias.indexOf(doc);
                const newFile = orias.slice();
                newFile.splice(index, 1);
                setOrias(newFile);
              }}
            >
              <Button icon={<UploadOutlined />}>{t('buttons.upload')}</Button>
            </Upload>
          </Form.Item>
          <Form.Item
            name="RIB"
            label={t('insurers.form.RIB')}
            rules={[{ required: true, message: t('errors.form.REQUIRED') }]}
            style={{ textAlign: 'right' }}
          >
            <Upload
              multiple
              maxCount={3}
              beforeUpload={(file) => {
                const newFile = [...rib];
                newFile.push(file);
                setRib(newFile);
                return false;
              }}
              onRemove={(doc) => {
                const index = rib.indexOf(doc);
                const newFile = rib.slice();
                newFile.splice(index, 1);
                setRib(newFile);
              }}
            >
              <Button icon={<UploadOutlined />}>{t('buttons.upload')}</Button>
            </Upload>
          </Form.Item>
          <Form.Item
            name={['address', 'number']}
            rules={[{ required: true, message: t('errors.form.REQUIRED') }]}
            label={<FieldNumberOutlined />}
            style={{ width: '180px', textAlign: 'left' }}
          >
            <Input placeholder={t('login.street-number')} />
          </Form.Item>
          <Form.Item
            name={['address', 'street']}
            rules={[{ required: true, message: t('errors.form.REQUIRED') }]}
            label={<NodeIndexOutlined />}
          >
            <Input placeholder={t('login.street-name')} />
          </Form.Item>
          <Form.Item
            name={['address', 'additional']}
            label={<HomeOutlined />}
            style={{ marginLeft: 8 }}
          >
            <Input placeholder={t('login.address-extra')} />
          </Form.Item>
          <Form.Item
            name={['address', 'postal_code']}
            rules={[{ required: true, message: t('errors.form.REQUIRED') }]}
            label={<CompassOutlined />}
            style={{ width: '180px', textAlign: 'left' }}
          >
            <Input
              onChange={handlePostalCodeChange}
              minLength={5}
              maxLength={5}
              autoComplete="off"
              placeholder={t('login.postal-code')}
            />
          </Form.Item>
          <Form.Item
            name={['address', 'city']}
            rules={[{ required: true, message: t('errors.form.REQUIRED') }]}
            label={<AimOutlined />}
            style={{ textAlign: 'left' }}
          >
            {!errorGetCities ? (
              <Select
                showSearch
                placeholder={t('login.city')}
                options={
                  cities &&
                  cities.map((city) => ({
                    label: city.label,
                    value: city.name
                  }))
                }
              />
            ) : (
              <Input placeholder={t('login.city')} />
            )}
          </Form.Item>
          <RegisterCondition />
        </>
      ) : (
        []
      )}
      {current === 2 ? (
        <>
          <Universign insurer={insurer} />
          <p
            style={{
              margin: '10px 0',
              fontSize: 15,
              color: '#fff',
              textTransform: 'uppercase',
              letterSpacing: '0.15em'
            }}
          >
            ou
          </p>
          <Popconfirm
            title={t('login.popconfirm.title')}
            okText={t('login.popconfirm.print')}
            okButtonProps={{ type: 'danger' }}
            cancelText={t('login.popconfirm.cancel')}
            onConfirm={() => printConvention()}
            icon={<WarningOutlined />}
          >
            <Button
              type="primary"
              style={{
                marginLeft: width < 1601 ? 10 : 0,
                marginTop: 0
              }}
              loading={isLoading}
            >
              <PrinterOutlined />
              {t('buttons.print_convention')}
            </Button>
          </Popconfirm>
        </>
      ) : (
        []
      )}
    </div>
  );
};

RegisterInput.propTypes = {
  handleSiretChange: PropTypes.func.isRequired,
  handleOriasChange: PropTypes.func.isRequired,
  handlePostalCodeChange: PropTypes.func.isRequired,
  getLayoutFromOriasStatus: PropTypes.func.isRequired,
  setCNI: PropTypes.func.isRequired,
  setKbis: PropTypes.func.isRequired,
  setOrias: PropTypes.func.isRequired,
  setRib: PropTypes.func.isRequired,
  oriasStatus: PropTypes.string.isRequired,
  siretStatus: PropTypes.bool.isRequired,
  enums: PropTypes.shape({
    genders: PropTypes.arrayOf(PropTypes.string),
    bics: PropTypes.arrayOf(PropTypes.string)
  }).isRequired,
  cities: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  current: PropTypes.number.isRequired,
  setEmail: PropTypes.func.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  insurer: PropTypes.object.isRequired,
  printConvention: PropTypes.func.isRequired,
  isLoading: PropTypes.bool.isRequired,
  orias: PropTypes.arrayOf.isRequired,
  cni: PropTypes.arrayOf.isRequired,
  kbis: PropTypes.arrayOf.isRequired,
  rib: PropTypes.arrayOf.isRequired,
  errorGetCities: PropTypes.bool.isRequired
};

export default RegisterInput;
